import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";
import { addPropertyMessage, onPropertyMessages } from "../../helpers/firestoreService";

/**
 * Group chat shared by everyone attached to a property — the owner, anyone the
 * property is shared with, and every tenant on it. There are no private threads.
 */
const PropertyChat = ({ propertyId, user, height = 340, placeholder = "Write a message…" }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!propertyId) return;
    setLoading(true);
    const unsubscribe = onPropertyMessages(propertyId, (msgs) => {
      setMessages(msgs);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [propertyId]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async () => {
    const body = text.trim();
    if (!body || !user?.uid) return;
    setError("");
    setSending(true);
    try {
      await addPropertyMessage(propertyId, {
        senderUid: user.uid,
        senderName: user.displayName || user.email || "",
        senderEmail: user.email || "",
        text: body,
      });
      setText("");
    } catch (e) {
      console.error("Send property message error:", e);
      setError("Message couldn't be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="d-flex flex-column">
      <div
        ref={boxRef}
        className="flex-grow-1 overflow-auto p-3 border rounded"
        style={{ height, background: "#fbfbfd" }}
      >
        {loading ? (
          <div className="text-center py-5"><Spinner size="sm" color="primary" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted py-5">
            <i className="bx bx-conversation" style={{ fontSize: "2.5rem", opacity: 0.3 }} />
            <p className="small mt-2 mb-0">No messages yet. Start the conversation.</p>
          </div>
        ) : (
          messages.map((m) => {
            const isSelf = m.senderUid === user?.uid;
            return (
              <div key={m.id} className={`d-flex mb-2 ${isSelf ? "justify-content-end" : "justify-content-start"}`}>
                <div
                  className={`rounded px-3 py-2 ${isSelf ? "bg-primary text-white" : "bg-white border"}`}
                  style={{ maxWidth: "80%", fontSize: "0.85rem" }}
                >
                  {!isSelf && (
                    <small className="d-block fw-semibold text-primary mb-1">
                      {m.senderName || m.senderEmail}
                    </small>
                  )}
                  <span style={{ whiteSpace: "pre-wrap" }}>{m.text}</span>
                  <div
                    className={`text-end mt-1 ${isSelf ? "text-white-50" : "text-muted"}`}
                    style={{ fontSize: "0.7rem" }}
                  >
                    {m.createdAt
                      ? m.createdAt.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                      : "Sending…"}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="pt-2">
        <div className="d-flex gap-2 mb-1">
          <Input
            type="textarea"
            rows={2}
            bsSize="sm"
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            style={{ resize: "none", borderRadius: "0.5rem" }}
          />
          <Button
            color="primary"
            size="sm"
            disabled={!text.trim() || sending}
            onClick={handleSend}
            style={{ minWidth: 58 }}
          >
            {sending ? <Spinner size="sm" /> : <i className="bx bx-send" />}
          </Button>
        </div>

        {error && <div className="text-danger small mb-1">{error}</div>}

        <small className="text-muted d-block" style={{ fontSize: "0.7rem" }}>
          <i className="bx bx-group me-1" />
          Everyone on this property — the owner, anyone it's shared with, and all
          tenants — can see this conversation. Shift+Enter for a new line.
          <br />
          By using this chat, you agree to our{" "}
          <a href="/community-guidelines" target="_blank" rel="noopener noreferrer">
            Community Guidelines
          </a>.
        </small>
      </div>
    </div>
  );
};

export default PropertyChat;
